import { FC, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { Alert, Snackbar, StrongModalText } from '@atoms';
import { useNamePartnerCustomers, useURLparams } from '@hooks';
import { FullWidthContent, MainMenuLayout } from '@layouts';
import { Table } from '@molecules';
import { DEFAULT_ROWS_PER_PAGE, pendingDispersionsColumns } from './constants';
import useGetPendingDispersions, {
  PendingDispersion,
  PendingDispersionStatus,
} from './data/useGetPendingDispersions';
import useResolvePendingDispersion from './data/useResolvePendingDispersion/hooks/useResolvePendingDispersion';
import SearchBoxPendingDispersions from './searchBox';
import { PendingDispersionsSearchFormValues } from './searchBox/constants';

interface PendingDispersionsParams {
  page: string;
  limit: string;
  status?: PendingDispersionStatus | '';
}

interface PendingDispersionRow {
  dispersion: PendingDispersion;
  callback: ReturnType<typeof useResolvePendingDispersion>['onActionClick'];
}

/**
 * Screen to approve or reject pending dispersion creations
 */
const PendingDispersionsScreen: FC = () => {
  const navigate = useNavigate();
  const { getNamePartnerCustomer } = useNamePartnerCustomers();
  const { params, setURLparams } = useURLparams<PendingDispersionsParams>({
    page: '1',
    limit: String(DEFAULT_ROWS_PER_PAGE),
  });

  const {
    data: response,
    isLoading,
    handlePageChange,
    handleRowsPerPageChange,
    handleRefresh,
  } = useGetPendingDispersions({
    params,
    setURLparams,
  });

  const {
    handleSubmit,
    onActionClick,
    isModalOpen,
    selectedDispersion,
    setIsModalOpen,
    isError,
    setIsError,
    setIsSuccess,
    isSuccess,
    alertMessage,
    isLoading: isLoadingAction,
  } = useResolvePendingDispersion(() => {
    handleRefresh();
  });

  const items = useMemo(() => {
    return (response?.page || []).map((item) => ({
      dispersion: {
        ...item,
        companyName: item.customerName || getNamePartnerCustomer(String(item.customerId)) || '-',
      },
      callback: onActionClick,
    }));
  }, [getNamePartnerCustomer, onActionClick, response?.page]);

  const rowsPerPage = Number(params?.limit || DEFAULT_ROWS_PER_PAGE);
  const filters: PendingDispersionsSearchFormValues = {
    status: params?.status || '',
  };

  /**
   * Applies status filter and resets to first page
   * @param nextFilters - Selected search filters
   */
  const handleSearch = (nextFilters: PendingDispersionsSearchFormValues) => {
    setURLparams({
      page: '1',
      limit: String(rowsPerPage),
      ...(nextFilters.status && { status: nextFilters.status }),
    });
  };

  /**
   * Navigates to the pending dispersion detail screen
   * @param row - Selected table row
   */
  const handleRowClick = (row: PendingDispersionRow) => {
    navigate(`/dispersiones/gestion-pendientes/${row.dispersion.id}`);
  };

  return (
    <MainMenuLayout>
      <FullWidthContent>
        <SearchBoxPendingDispersions filters={filters} onSearch={handleSearch} />
        <Table
          columns={pendingDispersionsColumns}
          count={response?.totalCount || 0}
          isLoading={isLoading}
          items={items}
          page={params?.page || 1}
          rowsPerPage={params?.limit || DEFAULT_ROWS_PER_PAGE}
          onItemClick={handleRowClick}
          //TODO: Cast the typing to avoid build errors.
          onPageChange={handlePageChange as () => void}
          onRowsPerPageChange={handleRowsPerPageChange as () => void}
        />
        <Alert
          variant='warning'
          modalVisible={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          handleSubmit={handleSubmit}
          closeButtonText='COMMON:CANCEL'
          title={t('PROSPECTS:SEND_EMAIL:MODAL:TITLE')}
          submitLoading={isLoadingAction}
          content={
            <Trans
              i18nKey={`PENDING_DISPERSIONS:CONFIRMATION_MODAL:${selectedDispersion.action}`}
              components={{ s: <StrongModalText /> }}
              values={selectedDispersion}
            />
          }
        />
        <Snackbar
          open={isError}
          message={t(alertMessage?.description)}
          severity={'error'}
          handleClose={() => setIsError(false)}
        />
        <Snackbar
          open={isSuccess}
          message={t(alertMessage?.description)}
          severity='success'
          handleClose={() => setIsSuccess(false)}
        />
      </FullWidthContent>
    </MainMenuLayout>
  );
};

export default PendingDispersionsScreen;
